import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {MediaQueryList | null} */
  #customMobileMediaQuery = null;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /**
   * Optional max-width breakpoint (px) to treat as "mobile" for this instance.
   * Example: data-mobile-max-width="768"
   * @returns {number | null}
   */
  get #mobileMaxWidth() {
    const value = this.dataset.mobileMaxWidth;
    if (!value) return null;

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Returns whether this accordion instance should behave as "mobile" right now.
   * @returns {boolean}
   */
  #isMobile() {
    const mobileMaxWidth = this.#mobileMaxWidth;

    if (mobileMaxWidth != null) {
      if (!this.#customMobileMediaQuery) {
        this.#customMobileMediaQuery = window.matchMedia(`(max-width: ${mobileMaxWidth}px)`);
      }

      return this.#customMobileMediaQuery.matches;
    }

    return isMobileBreakpoint();
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });

    // Keep default open state in sync with current breakpoint.
    if (this.#mobileMaxWidth != null) {
      // Ensure query exists before subscribing.
      this.#isMobile();
      this.#customMobileMediaQuery?.addEventListener('change', this.#handleMediaQueryChange, { signal });
    } else {
      mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = this.#isMobile();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = this.#isMobile();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
